import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { FirebaseError } from 'firebase/app';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '../../shared/ui/Button';
import { Input } from '../../shared/ui/Input';
import { FormField } from '../../shared/ui/FormField';
import { ThemeToggle } from '../../shared/ui/ThemeToggle';
import { useAuth } from './useAuth';
import { useToast } from '../../shared/hooks/useToast';
import { ArdixMark } from '../../shared/brand/ArdixMark';
import { usePublicIdentity } from '../../shared/brand/PublicIdentityContext';
import { ContractEndedScreen } from '../license/ContractEndedScreen';
import lema from '../../shared/brand/lema.svg';
import styles from './LoginView.module.css';

function mapFirebaseError(code: string): string {
  switch (code) {
    case 'auth/invalid-email':
      return 'Email inválido.';
    case 'auth/user-disabled':
      return 'Esta conta está desativada. Fale com o administrador.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Email ou senha incorretos.';
    case 'auth/too-many-requests':
      return 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';
    case 'auth/network-request-failed':
      return 'Falha de conexão. Verifique sua internet.';
    default:
      return 'Não foi possível entrar. Tente novamente em instantes.';
  }
}

export function LoginView() {
  const { status, signIn } = useAuth();
  const toast = useToast();
  const { clinicName, logoUrl, contractEnded } = usePublicIdentity();
  const [showEnded, setShowEnded] = useState(false);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});

  if (status === 'signed-in') {
    return <Navigate to="/" replace />;
  }
  if (showEnded && contractEnded) return <ContractEndedScreen actionLabel="Voltar" onSignOut={() => setShowEnded(false)} />;

  function validate(): boolean {
    const errors: { email?: string; password?: string } = {};
    if (!email.trim()) errors.email = 'Informe o email.';
    if (!password) errors.password = 'Informe a senha.';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      await signIn(email, password);
      // Navegação acontece via redirect quando status = signed-in
    } catch (error) {
      const code = error instanceof FirebaseError ? error.code : 'unknown';
      if (code === 'auth/user-disabled' && contractEnded) {
        setShowEnded(true);
        return;
      }
      toast.error(mapFirebaseError(code), { title: 'Erro ao entrar' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.page}>
      <aside className={styles.art}>
        <svg className={styles.arcs} viewBox="0 0 100 100" aria-hidden="true">
          {[12, 22, 32, 42].map((r) => (
            <circle key={r} cx="50" cy="50" r={r} fill="none" stroke="#F6F2EA" strokeWidth=".6" />
          ))}
        </svg>
        <ArdixMark tone="light" variant="full" className={styles.artMark} />
        <img src={lema} alt="Do eco ao laudo." className={styles.lema} />
      </aside>

      <main className={styles.panel}>
        <div className={styles.card}>
          {!clinicName && <h1 className={styles.srOnly}>Entrar</h1>}
          {clinicName && (
            <div className={styles.clinic}>
              {logoUrl && failedUrl !== logoUrl && (
                <img src={logoUrl} alt="" className={styles.clinicLogo} onError={() => setFailedUrl(logoUrl)} />
              )}
              <h1 className={styles.title}>{clinicName}</h1>
              <p className={styles.subtitle}>Entre com sua conta da clínica</p>
            </div>
          )}

          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            <FormField label="Email" required error={fieldErrors.email}>
              {(id) => (
                <Input
                  id={id}
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  invalid={Boolean(fieldErrors.email)}
                  disabled={submitting}
                />
              )}
            </FormField>

            <FormField label="Senha" required error={fieldErrors.password}>
              {(id) => (
                <Input
                  id={id}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  invalid={Boolean(fieldErrors.password)}
                  disabled={submitting}
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      className={styles['eye-btn']}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  }
                />
              )}
            </FormField>

            <div className={styles['password-row']}>
              <button
                type="button"
                className={styles.forgot}
                onClick={() =>
                  toast.info(
                    'Peça ao administrador para forçar a troca da sua senha. Ele vai gerar uma senha provisória.',
                    { title: 'Esqueci minha senha' }
                  )
                }
              >
                Esqueci minha senha
              </button>
            </div>

            <Button type="submit" isLoading={submitting} fullWidth>
              Entrar
            </Button>
          </form>

          <div className={styles.theme}>
            <ThemeToggle />
          </div>
        </div>
      </main>
    </div>
  );
}
